import { useEffect, useRef } from 'react';
import type { PublicSurvey } from '../types.js';

interface AutosaveInput {
  survey: PublicSurvey | null;
  responses: Record<string, unknown>;
  email: string;
  enabled: boolean;
  saveProgress: (input: { survey: PublicSurvey; responses: Record<string, unknown>; email: string }) => Promise<void>;
  delayMs?: number;
}

const DEFAULT_DEBOUNCE_MS = 1200;

export function useAutosaveProgress(input: AutosaveInput): void {
  const { survey, responses, email, enabled, saveProgress, delayMs } = input;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousResponsesRef = useRef<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (!survey || !enabled) return;
    if (!hasAnyAnswer(responses)) return;
    if (previousResponsesRef.current === responses) return;
    previousResponsesRef.current = responses;

    if (timerRef.current) clearTimeout(timerRef.current);
    const handle = setTimeout(() => {
      void saveProgress({ survey, responses, email });
    }, delayMs ?? DEFAULT_DEBOUNCE_MS);
    timerRef.current = handle;

    return () => clearTimeout(handle);
  }, [survey, responses, email, enabled, saveProgress, delayMs]);
}

function hasAnyAnswer(responses: Record<string, unknown>): boolean {
  for (const value of Object.values(responses)) {
    if (value == null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    return true;
  }
  return false;
}
